import React from 'react'
import moment from 'moment'

import Pager from './Pager'
import usePagination, { DEFAULT_PAGE_SIZE } from './usePagination'
import { heatLevel } from '../activityReport'

/**
 * Rows (users, or boards) x time-bucket intensity grid.
 *
 * Magnitude is the job here, so this is a sequential single-hue ramp rather than
 * categorical colour. Cells carry a `viz-heat--n` class instead of a computed
 * background, which keeps colour in the stylesheet and gives the ramp exactly
 * seven discrete steps.
 *
 * Counts live in the cell title rather than printed in the cell: at a week's
 * width the cells are too small for legible numerals, and the darker steps would
 * fail contrast against dark text.
 *
 * Columns are `periods` (from `buildHeatmap`'s granularity-aware `{ key, label,
 * start, end }` buckets) when the caller passes them, falling back to plain
 * `MMM D` day labels built from `days` for a caller (or test) that only ever
 * knew about day columns. A single-day bucket (`start === end`, true for every
 * day-granularity column and any week/month bucket clamped to one day at the
 * edge of the range) keeps today's two-line `D` / `MMM` header; a genuine
 * multi-day bucket renders its own label on one line instead.
 */
const ActivityHeatmap = ({ days, periods, grid, peak, pageSize = DEFAULT_PAGE_SIZE, rowKey = 'userName', rowHeader = 'User', unit = 'users' }) => {
    // Rows are paged so the card stays a bounded height; the day columns still
    // scroll horizontally, which keeps the user column pinned beside them.
    const { pageItems, page, pageCount, setPage, start, total } = usePagination(grid, pageSize)

    const columns = periods || days.map((day) => ({ key: day, label: moment(day).format('MMM D') }))
    const periodByKey = {}
    columns.forEach((period) => {
        periodByKey[period.key] = period
    })

    return (
        <>
            <div className="viz-heatmap-scroll">
                <table className="viz-heatmap">
                    <thead>
                        <tr>
                            <th className="viz-heatmap-corner">{rowHeader}</th>
                            {columns.map((period) => {
                                const isBucket = period.start !== undefined && period.start !== period.end
                                const title = isBucket
                                    ? `${moment(period.start).format('dddd, MMM D YYYY')} – ${moment(period.end).format('dddd, MMM D YYYY')}`
                                    : moment(period.key).format('dddd, MMM D YYYY')
                                return (
                                    <th key={period.key} className="viz-heatmap-day" title={title}>
                                        {isBucket ? (
                                            <span className="viz-heatmap-day-label">{period.label}</span>
                                        ) : (
                                            <>
                                                <span className="viz-heatmap-day-label">{moment(period.key).format('D')}</span>
                                                <span className="viz-heatmap-day-month">{moment(period.key).format('MMM')}</span>
                                            </>
                                        )}
                                    </th>
                                )
                            })}
                            <th className="viz-heatmap-total">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageItems.map((row) => (
                            <tr key={row[rowKey]}>
                                <th className="viz-heatmap-user" title={row[rowKey]}>{row[rowKey]}</th>
                                {row.cells.map((cell) => {
                                    const cellLabel = periodByKey[cell.day]?.label || moment(cell.day).format('MMM D')
                                    return (
                                        <td
                                            key={cell.day}
                                            className={`viz-heat viz-heat--${heatLevel(cell.count, peak)}`}
                                            title={`${row[rowKey]} — ${cellLabel}: ${cell.count} action${cell.count === 1 ? '' : 's'}`}
                                        >
                                            <span className="viz-visually-hidden">{cell.count}</span>
                                        </td>
                                    )
                                })}
                                <td className="viz-heatmap-total">{row.total.toLocaleString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="viz-heatmap-legend">
                    <span className="viz-heatmap-legend-label">Less</span>
                    {[0, 1, 2, 3, 4, 5, 6].map((level) => (
                        <span key={level} className={`viz-heat-key viz-heat--${level}`} />
                    ))}
                    <span className="viz-heatmap-legend-label">More</span>
                    <span className="viz-heatmap-legend-peak">Peak {peak.toLocaleString()} in a day</span>
                </div>
            </div>
            <Pager
                page={page}
                pageCount={pageCount}
                setPage={setPage}
                start={start}
                count={pageItems.length}
                total={total}
                unit={unit}
            />
        </>
    )
}

export default ActivityHeatmap
